<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useDisplayModeStore } from "../stores/useDisplayModeStore";
import type { ZwStatusBar } from "../lib/zwConfig";
import ZwCheckbox from "./ui/ZwCheckbox.vue";

const statusBar = defineModel<ZwStatusBar>("statusBar", { required: true });

const display = useDisplayModeStore();
const { typewriterPref } = storeToRefs(display);

/** Replace the whole bar so TEA `shallowRef` parents see a new value. */
function patchStatusBar(patch: Partial<ZwStatusBar>): void {
  statusBar.value = { ...statusBar.value, ...patch };
}
</script>

<template>
  <section
    class="project-settings-group"
    aria-labelledby="settings-status-heading"
  >
    <h3 id="settings-status-heading" class="project-settings-subhead">
      Status bar
    </h3>
    <p class="project-settings-hint">
      Choose what appears in the editor’s status bar.
    </p>
    <div class="project-settings-checks">
      <ZwCheckbox
        :model-value="statusBar.wordCount"
        @update:model-value="patchStatusBar({ wordCount: $event })"
      >
        Word count
      </ZwCheckbox>
      <ZwCheckbox
        :model-value="statusBar.readingTime"
        @update:model-value="patchStatusBar({ readingTime: $event })"
      >
        Reading time
      </ZwCheckbox>
      <ZwCheckbox
        :model-value="statusBar.goals"
        @update:model-value="patchStatusBar({ goals: $event })"
      >
        Writing goals
      </ZwCheckbox>
      <ZwCheckbox
        :model-value="statusBar.zoom"
        @update:model-value="patchStatusBar({ zoom: $event })"
      >
        Interface zoom
      </ZwCheckbox>
    </div>
  </section>

  <section
    class="project-settings-group"
    aria-labelledby="settings-focus-heading"
  >
    <h3 id="settings-focus-heading" class="project-settings-subhead">
      Focus mode
    </h3>
    <p class="project-settings-hint">
      Keep the paragraph you’re writing centered in focus mode.
    </p>
    <div class="project-settings-checks">
      <ZwCheckbox
        :model-value="typewriterPref"
        @update:model-value="display.toggleTypewriterPref()"
      >
        Typewriter scrolling
      </ZwCheckbox>
    </div>
  </section>

  <section
    class="project-settings-group"
    aria-labelledby="settings-scene-peeks-heading"
  >
    <h3 id="settings-scene-peeks-heading" class="project-settings-subhead">
      Neighboring scenes
    </h3>
    <p class="project-settings-hint">
      Preview the previous and next scenes while you write. Hidden in focus
      mode.
    </p>
    <div class="project-settings-checks">
      <ZwCheckbox
        :model-value="statusBar.showNeighborScenePeeks"
        @update:model-value="patchStatusBar({ showNeighborScenePeeks: $event })"
      >
        Show neighboring scenes
      </ZwCheckbox>
    </div>
  </section>
</template>
